import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, AlertTriangle, ChevronDown, EyeOff, FlaskConical, Search, X } from 'lucide-react';
import type { DemoSurfaceKind, DemoSurfaceRef } from '../../../api/Navigation/moduleConfigAdmin';
import type { IconName } from '../../../components/AppIcon';
import { Checkbox } from '../../../components/ui/Checkbox';
import { StatusBadge } from '../../../components/ui/StatusBadge';
import ConfigSection, { type ConfigItemModel, type ConfigRowModel } from './ConfigSection';
import type { DemoAccessStatus, DemoTenantKind } from './useModuleConfigEditor';

/**
 * The one grouped switch table of the Module Configuration page (CO-864): Web, Mobile, Page tabs and
 * Capabilities in a single bordered list, with a search box, two filter rows and expand / collapse
 * all above it. It replaced four stacks of identical cards.
 *
 * This component owns only what is shown: the query, the two filters and which items are open. The
 * values behind the switches live in `useModuleConfigEditor`. The page mounts it with a `key` of the
 * company id, so a company switch resets the search and closes every item.
 */

export type SwitchTableSectionId = 'web' | 'mobile' | 'tabs' | 'capabilities';
export type SwitchStateFilter = 'on' | 'off' | 'changed' | 'unset';

/**
 * What the page knows about a row beyond what `ConfigSection` draws today (CO-861).
 *
 *  - `stored`: the baseline holds a stored row for the key. `false` means the value shown is only the
 *    default ("On, no row"). Undefined where the question does not apply (locked rows, demo rows).
 *  - `stateLabel`: the same fact as translated words ("On, set", "Off, set", "On, no row").
 *  - `demo`: the row is a demo surface; its switch mirrors the grant and cannot be edited here.
 *
 * `stored` drives the "No stored row" filter in this file. `stateLabel` and `demo` are carried for
 * `ConfigSection`'s row to draw; that file belongs to another lane, see the lane hand-over.
 */
export interface SwitchRowExtras {
  stored?: boolean;
  stateLabel?: string;
  demo?: boolean;
}
export type SwitchRowModel = ConfigRowModel & SwitchRowExtras;

export interface SwitchTableSection {
  id: SwitchTableSectionId;
  title: string;
  icon?: IconName;
  items: ConfigItemModel[];
}

interface SwitchTableProps {
  sections: SwitchTableSection[];
}

const STATE_FILTERS: SwitchStateFilter[] = ['on', 'off', 'changed', 'unset'];

const expandKey = (sectionId: string, itemId: string) => `${sectionId}/${itemId}`;

function passesState(
  state: SwitchStateFilter | null,
  checked: boolean,
  changed: boolean,
  stored?: boolean
): boolean {
  if (state === null) return true;
  if (state === 'changed') return changed;
  // Only a row that is known to hold no stored row. A module switch carries no such fact.
  if (state === 'unset') return stored === false;
  return state === 'on' ? checked : !checked;
}

/** Translated label, stored path and storage key (which starts with the module id). */
const rowHaystack = (row: ConfigRowModel) => `${row.label} ${row.path ?? ''} ${row.key}`.toLowerCase();

/**
 * The item with only the rows that pass, or null when nothing of it does. A query that matches the
 * item itself (title or id) keeps all of its rows, so searching "payroll" shows the Payroll module
 * whole and, elsewhere, any single row that mentions it.
 */
function filterItem(item: ConfigItemModel, query: string, state: SwitchStateFilter | null): ConfigItemModel | null {
  if (query === '' && state === null) return item;

  const itemHit = query === '' || `${item.title} ${item.id}`.toLowerCase().includes(query);
  const groups = item.groups.map(group => {
    const groupHit = itemHit || (group.heading ?? '').toLowerCase().includes(query);
    return {
      ...group,
      rows: group.rows.filter(
        row =>
          passesState(state, row.checked, row.changed === true, (row as SwitchRowModel).stored) &&
          (groupHit || rowHaystack(row).includes(query))
      ),
    };
  });
  if (groups.some(group => group.rows.length > 0)) return { ...item, groups };

  // No row passes. The item still shows when it was matched by name and its own switch passes.
  const selfPasses = item.toggle
    ? passesState(state, item.toggle.checked, item.changed === true)
    : state === null;
  return itemHit && selfPasses ? { ...item, groups } : null;
}

const chipClass = (pressed: boolean) =>
  `inline-flex min-h-8 items-center whitespace-nowrap rounded-full border px-3 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background ${
    pressed
      ? 'border-primary bg-primary/10 text-primary'
      : 'border-border bg-card text-muted-foreground hover:bg-muted hover:text-foreground'
  }`;

const textButtonClass =
  'rounded-md px-2 py-1 text-xs font-medium text-primary hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50';

/** Placeholder with the table's own row rhythm. Named for assistive technology by `label`. */
export function SwitchTableSkeleton({ label }: { label: string }) {
  return (
    <div role="status" aria-label={label} data-testid="module-config-loading" className="animate-pulse motion-reduce:animate-none">
      <div className="mb-3 h-9 w-full max-w-sm rounded-lg bg-accent" />
      <div className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
        {[72, 48, 64, 40, 56, 44, 60, 52].map((width, index) => (
          <div key={index} className="flex h-10 items-center gap-3 px-4">
            <div className="h-3 rounded bg-accent" style={{ width: `${width / 2}%` }} />
            <div className="ms-auto h-5 w-9 rounded-full bg-accent" />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function SwitchTable({ sections }: SwitchTableProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [sectionFilter, setSectionFilter] = useState<SwitchTableSectionId | null>(null);
  const [stateFilter, setStateFilter] = useState<SwitchStateFilter | null>(null);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set());

  const query = search.trim().toLowerCase();
  const filterActive = query !== '' || sectionFilter !== null || stateFilter !== null;

  const visibleSections = useMemo(
    () =>
      sections
        .filter(section => sectionFilter === null || section.id === sectionFilter)
        .map(section => ({
          ...section,
          items: section.items
            .map(item => filterItem(item, query, stateFilter))
            .filter((item): item is ConfigItemModel => item !== null),
        }))
        .filter(section => section.items.length > 0),
    [sections, sectionFilter, query, stateFilter]
  );

  const visibleKeys = (onlyWithRows: boolean) =>
    visibleSections.flatMap(section =>
      section.items
        .filter(item => !onlyWithRows || item.groups.some(group => group.rows.length > 0))
        .map(item => expandKey(section.id, item.id))
    );

  // A search or a state filter opens what it found, once per change of the filter itself. It reads
  // the latest result through a ref because the models are rebuilt on every toggle, and a toggle
  // must not re-open an item the operator has just closed.
  const visibleKeysRef = useRef(visibleKeys);
  visibleKeysRef.current = visibleKeys;
  useEffect(() => {
    if (query === '' && stateFilter === null) return;
    setExpanded(new Set(visibleKeysRef.current(true)));
  }, [query, stateFilter, sectionFilter]);

  const toggleExpanded = (sectionId: string, itemId: string) => {
    const key = expandKey(sectionId, itemId);
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const clearFilters = () => {
    setSearch('');
    setSectionFilter(null);
    setStateFilter(null);
  };

  return (
    <div className="mb-6" data-testid="module-config-table">
      <div className="mb-3 flex flex-col gap-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative min-w-0 flex-1 sm:max-w-sm">
            <Search
              aria-hidden="true"
              className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground ltr:left-3 rtl:right-3"
            />
            <input
              type="search"
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder={t('moduleConfig.search.placeholder')}
              aria-label={t('moduleConfig.search.label')}
              data-testid="module-config-search"
              className="h-9 w-full rounded-lg border border-input bg-card pe-3 ps-9 text-sm text-foreground placeholder:text-muted-foreground focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
            />
          </div>
          <div className="flex items-center gap-1 sm:ms-auto">
            <button
              type="button"
              className={textButtonClass}
              data-testid="module-config-expand-all"
              onClick={() => setExpanded(new Set(visibleKeys(false)))}
              disabled={visibleSections.length === 0}
            >
              {t('moduleConfig.expandAll')}
            </button>
            <button
              type="button"
              className={textButtonClass}
              data-testid="module-config-collapse-all"
              onClick={() => setExpanded(new Set())}
              disabled={expanded.size === 0}
            >
              {t('moduleConfig.collapseAll')}
            </button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <div role="group" aria-label={t('moduleConfig.filter.sectionLabel')} className="flex flex-wrap items-center gap-1.5">
            {sections.map(section => {
              const pressed = sectionFilter === section.id;
              return (
                <button
                  key={section.id}
                  type="button"
                  aria-pressed={pressed}
                  data-testid={`module-config-filter-${section.id}`}
                  className={chipClass(pressed)}
                  onClick={() => setSectionFilter(pressed ? null : section.id)}
                >
                  {section.title}
                </button>
              );
            })}
          </div>
          <div role="group" aria-label={t('moduleConfig.filter.stateLabel')} className="flex flex-wrap items-center gap-1.5">
            {STATE_FILTERS.map(state => {
              const pressed = stateFilter === state;
              return (
                <button
                  key={state}
                  type="button"
                  aria-pressed={pressed}
                  data-testid={`module-config-filter-${state}`}
                  className={chipClass(pressed)}
                  onClick={() => setStateFilter(pressed ? null : state)}
                >
                  {t(`moduleConfig.filter.${state}`)}
                </button>
              );
            })}
          </div>
          {filterActive && (
            <button type="button" className={`${textButtonClass} inline-flex items-center gap-1`} onClick={clearFilters}>
              <X aria-hidden="true" className="h-3.5 w-3.5" />
              {t('moduleConfig.filter.clear')}
            </button>
          )}
        </div>
      </div>

      {visibleSections.length === 0 ? (
        <div
          role="status"
          data-testid="module-config-no-match"
          className="rounded-lg border border-dashed border-border px-4 py-10 text-center text-sm text-muted-foreground"
        >
          {t('moduleConfig.search.noMatch')}
        </div>
      ) : (
        // `relative`: load-bearing. It is the positioned ancestor for anything absolutely positioned
        // inside the rows (tooltip anchors, a switch's hidden input), which would otherwise escape
        // `<main>` and stretch the document.
        <div className="relative divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
          {visibleSections.map(section => {
            const prefix = `${section.id}/`;
            const expandedIds = new Set(
              Array.from(expanded)
                .filter(key => key.startsWith(prefix))
                .map(key => key.slice(prefix.length))
            );
            return (
              <ConfigSection
                key={section.id}
                id={section.id}
                title={section.title}
                icon={section.icon}
                items={section.items}
                expandedIds={expandedIds}
                onToggleExpanded={itemId => toggleExpanded(section.id, itemId)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ── Demo surfaces of the open company: the grant and revoke control (CO-849, CO-861). ── */

/** One key of the demo list, named and resolved against the registry by the page. */
export interface DemoSurfaceRow {
  /** `demoSurfaceId` of the surface. Unique in the list. */
  id: string;
  surface: DemoSurfaceRef;
  kind: DemoSurfaceKind;
  /** Translated name; the stored key itself when the registry does not hold the surface. */
  label: string;
  /** The stored key (a route, a tab id or a module id), shown beside the name when it differs. */
  path: string;
  /** Translated module or tab group the surface sits in. */
  owner?: string;
  granted: boolean;
  /** Translated name of the module that is saved OFF and hides the surface although it is granted. */
  hiddenBy?: string;
}

interface DemoSurfacePanelProps {
  companyName: string;
  status: DemoAccessStatus;
  /** The read was refused (401 or 403): no retry, the answer will not change. */
  refused: boolean;
  rows: DemoSurfaceRow[];
  tenantKind: DemoTenantKind;
  /** Granting asks for an explicit confirmation first: a live company, or one the server could not classify. */
  confirmGrant: boolean;
  pendingId: string | null;
  actionError: { id: string; reason: string | null } | null;
  /** A config save or reload is in flight; one write at a time on a tenant. */
  disabled: boolean;
  onGrant: (surface: DemoSurfaceRef) => void;
  onRevoke: (surface: DemoSurfaceRef) => void;
  onDismissError: () => void;
  onRetry: () => void;
}

const rowButtonBase =
  'inline-flex min-h-8 shrink-0 items-center justify-center whitespace-nowrap rounded-lg border px-3 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50';
const grantButtonClass = `${rowButtonBase} border-border bg-card text-foreground hover:bg-muted`;
// Revoking takes a page away from another company's users, so it reads as destructive.
const revokeButtonClass = `${rowButtonBase} border-destructive/40 bg-card text-destructive hover:bg-destructive/10`;
const confirmButtonClass = `${rowButtonBase} border-primary bg-primary text-primary-foreground hover:bg-primary/90`;

/**
 * Which demo surfaces the open company's users can see, and the control that changes it.
 *
 * A demo surface is shown by an active grant alone (CO-849); the stored switch for the key decides
 * nothing. So this sits apart from the switch table and its staged changes: Grant and Revoke are
 * single requests applied at once. Granting to a live company asks first, inline and by name, with
 * one required tick; a demo tenant or a test company is granted directly, and revoking never asks.
 * No dialog: the question is asked on the row it is about.
 */
export function DemoSurfacePanel({
  companyName,
  status,
  refused,
  rows,
  tenantKind,
  confirmGrant,
  pendingId,
  actionError,
  disabled,
  onGrant,
  onRevoke,
  onDismissError,
  onRetry,
}: DemoSurfacePanelProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  /** The row whose live-company confirmation is open, and whether its box is ticked. */
  const [confirming, setConfirming] = useState<string | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);

  const visibleCount = rows.filter(row => row.granted && !row.hiddenBy).length;
  const busy = pendingId !== null;
  const locked = disabled || busy;
  const headingId = 'module-config-demo-heading';
  const panelId = 'module-config-demo-panel';

  // A failed write is reported inside the list, so the list opens to show it.
  useEffect(() => {
    if (actionError) setOpen(true);
  }, [actionError]);

  const closeConfirm = () => {
    setConfirming(null);
    setAcknowledged(false);
  };
  const askOrGrant = (row: DemoSurfaceRow) => {
    onDismissError();
    if (!confirmGrant) {
      onGrant(row.surface);
      return;
    }
    setAcknowledged(false);
    setConfirming(row.id);
  };

  if (status === 'failed') {
    return (
      <div
        role="alert"
        data-testid="module-config-demo-load-error"
        className="mb-6 flex flex-wrap items-center gap-3 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
      >
        <AlertCircle aria-hidden="true" className="h-4 w-4 shrink-0" />
        <span className="min-w-0 flex-1">
          {refused ? t("You don't have permission to perform this action.") : t('moduleConfig.demo.loadFailed')}
        </span>
        {!refused && (
          <button type="button" onClick={onRetry} className={grantButtonClass}>
            {t('Retry')}
          </button>
        )}
      </div>
    );
  }
  if (status !== 'ready') return null;

  return (
    <section
      aria-labelledby={headingId}
      data-testid="module-config-demo"
      data-visible-count={visibleCount}
      data-tenant-kind={tenantKind}
      className="mb-6 overflow-hidden rounded-lg border border-border bg-card"
    >
      <h2 id={headingId}>
        <button
          type="button"
          data-testid="module-config-demo-toggle"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => setOpen(previous => !previous)}
          className="flex w-full flex-wrap items-center gap-x-2 gap-y-1 px-4 py-2.5 text-start transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
        >
          <ChevronDown
            aria-hidden="true"
            className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 motion-reduce:transition-none ${
              open ? '' : '-rotate-90 rtl:rotate-90'
            }`}
          />
          <FlaskConical aria-hidden="true" className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="text-sm font-medium text-foreground">{t('moduleConfig.demo.title')}</span>
          {/* Words and an icon, never colour alone: above zero the company's users see sample data. */}
          <span
            data-testid="module-config-demo-count"
            className={`inline-flex min-w-0 items-center gap-1 text-xs ${
              visibleCount > 0 ? 'font-medium text-warning-strong' : 'text-muted-foreground'
            }`}
          >
            {visibleCount > 0 && <AlertTriangle aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />}
            {visibleCount > 0
              ? t('moduleConfig.demo.visibleCount', { count: visibleCount, company: companyName })
              : t('moduleConfig.demo.visibleNone', { company: companyName })}
          </span>
        </button>
      </h2>

      <div id={panelId} hidden={!open} className="border-t border-border">
        {open && (
          <>
            <div className="space-y-1 bg-muted px-4 py-2.5 text-xs leading-relaxed text-muted-foreground">
              <p className="max-w-[75ch]">{t('moduleConfig.demo.intro')}</p>
              <p data-testid="module-config-demo-tenant-kind" className="font-medium text-foreground">
                {t(`moduleConfig.demo.tenant.${tenantKind}`)}
              </p>
            </div>

            {rows.length === 0 ? (
              <p className="px-4 py-6 text-center text-sm text-muted-foreground">{t('moduleConfig.demo.empty')}</p>
            ) : (
              <ul className="divide-y divide-border">
                {rows.map(row => {
                  const pending = pendingId === row.id;
                  const failed = actionError?.id === row.id;
                  const asking = confirming === row.id && !row.granted;
                  return (
                    <li
                      key={row.id}
                      data-testid={`module-config-demo-row-${row.id}`}
                      data-granted={row.granted ? 'true' : 'false'}
                      className="px-4 py-2"
                    >
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-0.5">
                          <span className="min-w-0 truncate text-sm text-foreground">{row.label}</span>
                          {row.path !== row.label && (
                            // A route or a storage key, so it reads left to right in every language.
                            <span dir="ltr" className="min-w-0 truncate font-mono text-xs text-muted-foreground">
                              {row.path}
                            </span>
                          )}
                          <span className="shrink-0 text-xs text-muted-foreground">
                            {row.owner
                              ? t('moduleConfig.demo.kindIn', {
                                  kind: t(`moduleConfig.demo.kind.${row.kind}`),
                                  owner: row.owner,
                                })
                              : t(`moduleConfig.demo.kind.${row.kind}`)}
                          </span>
                          {row.granted && row.hiddenBy && (
                            <span className="inline-flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
                              <EyeOff aria-hidden="true" className="h-3 w-3" />
                              {t('moduleConfig.demo.hiddenBy', { module: row.hiddenBy })}
                            </span>
                          )}
                        </div>
                        <StatusBadge
                          variant={row.granted ? 'warning' : 'neutral'}
                          size="sm"
                          withDot={row.granted}
                          className="shrink-0"
                        >
                          {row.granted ? t('moduleConfig.demo.granted') : t('moduleConfig.demo.notGranted')}
                        </StatusBadge>
                        {row.granted ? (
                          <button
                            type="button"
                            className={revokeButtonClass}
                            disabled={locked}
                            aria-busy={pending}
                            aria-label={t('moduleConfig.demo.revokeLabel', { name: row.label, company: companyName })}
                            data-testid={`module-config-demo-revoke-${row.id}`}
                            onClick={() => {
                              closeConfirm();
                              onRevoke(row.surface);
                            }}
                          >
                            {t('moduleConfig.demo.revoke')}
                          </button>
                        ) : (
                          <button
                            type="button"
                            className={grantButtonClass}
                            disabled={locked || asking}
                            aria-busy={pending}
                            aria-label={t('moduleConfig.demo.grantLabel', { name: row.label, company: companyName })}
                            data-testid={`module-config-demo-grant-${row.id}`}
                            onClick={() => askOrGrant(row)}
                          >
                            {t('moduleConfig.demo.grant')}
                          </button>
                        )}
                      </div>

                      {asking && (
                        <div
                          role="group"
                          aria-label={t('moduleConfig.demo.confirm.submit', { company: companyName })}
                          data-testid="module-config-demo-confirm"
                          className="mt-2 space-y-2 rounded-lg border border-warning/40 bg-warning/10 px-3 py-2.5"
                        >
                          <p className="flex max-w-[75ch] items-start gap-2 text-sm text-foreground">
                            <AlertTriangle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-warning-strong" />
                            <span>
                              {t(
                                tenantKind === 'unknown'
                                  ? 'moduleConfig.demo.confirm.bodyUnknown'
                                  : 'moduleConfig.demo.confirm.body',
                                { name: row.label, company: companyName }
                              )}
                            </span>
                          </p>
                          <Checkbox
                            checked={acknowledged}
                            disabled={locked}
                            onChange={event => setAcknowledged(event.target.checked)}
                            label={t('moduleConfig.demo.confirm.checkbox', { company: companyName })}
                            data-testid="module-config-demo-confirm-checkbox"
                          />
                          <div className="flex flex-wrap items-center gap-2">
                            <button
                              type="button"
                              className={confirmButtonClass}
                              disabled={!acknowledged || locked}
                              aria-busy={pending}
                              data-testid="module-config-demo-confirm-submit"
                              onClick={() => onGrant(row.surface)}
                            >
                              {t('moduleConfig.demo.confirm.submit', { company: companyName })}
                            </button>
                            <button type="button" className={grantButtonClass} disabled={busy} onClick={closeConfirm}>
                              {t('Cancel')}
                            </button>
                          </div>
                        </div>
                      )}

                      {failed && (
                        <div
                          role="alert"
                          data-testid="module-config-demo-action-error"
                          className="mt-2 flex flex-wrap items-center gap-3 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                        >
                          <AlertCircle aria-hidden="true" className="h-4 w-4 shrink-0" />
                          <span className="min-w-0 flex-1">
                            {actionError?.reason
                              ? t('moduleConfig.demo.actionFailedWithReason', { reason: actionError.reason })
                              : t('moduleConfig.demo.actionFailed')}
                          </span>
                          <button
                            type="button"
                            className="shrink-0 rounded-md p-1 hover:bg-destructive/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            aria-label={t('Close')}
                            onClick={onDismissError}
                          >
                            <X aria-hidden="true" className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}
      </div>
    </section>
  );
}
